import { useState } from "react";
import { useLocation, Link } from "react-router-dom";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { toast } from "sonner";
import { Zap, Truck, CheckCircle2, MessageCircle, Loader2, ExternalLink, ShoppingBag, ImagePlus, X, Package, FileText } from "lucide-react";

const eur = (n) => `${Number(n).toFixed(2).replace(".", ",")} €`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ZONE = { napoli: { label: "Napoli e Provincia", costo: 3.5 }, italia: { label: "Resto d'Italia", costo: 6.0 }, ritiro: { label: "Ritiro di persona", costo: 0 } };

export default function Preventivatore() {
  const location = useLocation();
  const prev = location.state?.preventivo || null;
  const [righe] = useState(prev?.righe || []);
  const [zona] = useState(prev?.zona || "napoli");
  const [preventivoId] = useState(prev?.id || null);
  const [numero] = useState(prev?.numero || null);
  const [whatsapp, setWhatsapp] = useState(prev?.whatsapp || "");
  const [immagine, setImmagine] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [risultato, setRisultato] = useState(null);

  const subtotale = righe.reduce((a, r) => a + r.prezzo_unitario * r.quantita, 0);
  const isRitiro = zona === "ritiro";
  const spedGratis = !isRitiro && subtotale > 39;
  const spedizione = isRitiro || spedGratis ? 0 : ZONE[zona].costo;
  const totale = subtotale + spedizione;

  const onSelectImage = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/(png|jpe?g|webp|gif)$/i.test(f.name)) return toast.error("Formato non supportato. Usa JPG, PNG, WEBP o GIF.");
    if (f.size > 8 * 1024 * 1024) return toast.error("Immagine troppo grande (max 8 MB).");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/vendita/upload-immagine", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImmagine({ url: `${BACKEND}${data.url_relativa}`, name: f.name });
      toast.success("Immagine caricata.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore durante il caricamento dell'immagine.");
    } finally { setUploading(false); }
  };

  const buildMessage = (invoiceUrl) => `Ciao! Ecco il link sicuro per completare il pagamento del tuo ordine personalizzato: ${invoiceUrl}\n\n📧 NB: in fase di pagamento inserisci la tua migliore email, così potrai ricevere tutti gli aggiornamenti sul tuo ordine.\n\nGrazie dallo staff Gigli Edition!`;

  const submit = async () => {
    if (righe.length === 0) return toast.error("Nessun articolo. Trasferisci prima un preventivo dalla sezione Preventivi.");
    const tel = whatsapp.replace(/\D/g, "");
    if (tel.length < 8) return toast.error("Inserisci un numero WhatsApp valido (con prefisso paese, es. 39...).");
    const waWindow = window.open("", "_blank");
    setLoading(true);
    setRisultato(null);
    try {
      const { data } = await api.post("/shopify/draft-order", { righe, zona, immagine_url: immagine?.url || null, whatsapp: tel, preventivo_id: preventivoId });
      const invoiceUrl = data.invoice_url;
      if (!invoiceUrl) throw new Error("Nessun link di pagamento ricevuto da Shopify.");
      const waUrl = `https://wa.me/${tel}?text=${encodeURIComponent(buildMessage(invoiceUrl))}`;
      setRisultato({ invoiceUrl, waUrl, nome: data.nome });
      if (waWindow) waWindow.location.href = waUrl;
      toast.success("Ordine creato! Reindirizzamento a WhatsApp...");
    } catch (e) {
      if (waWindow) waWindow.close();
      toast.error(e.response?.data?.detail || e.message || "Errore nella creazione dell'ordine.");
    } finally { setLoading(false); }
  };

  return (
    <Layout>
      <div className="mb-6 flex items-center gap-2.5">
        <div className="w-10 h-10 rounded-xl bg-emerald-400/15 border border-emerald-400/30 flex items-center justify-center">
          <Zap className="w-5 h-5 text-emerald-400" />
        </div>
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-100 tracking-tight">Vendita Rapida</h1>
          <p className="text-slate-500 text-sm">Crea il link di pagamento Shopify e invialo al cliente via WhatsApp</p>
        </div>
      </div>

      {righe.length === 0 ? (
        <div data-testid="vr-empty" className="glass rounded-2xl p-8 text-center">
          <ShoppingBag className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-300 font-semibold mb-1">Nessun articolo da vendere</p>
          <p className="text-slate-500 text-sm mb-4">Crea un preventivo e, dopo l'approvazione del cliente, trasferiscilo qui dalla sezione Preventivi.</p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Link data-testid="vr-go-calc" to="/preventivatore" className="inline-flex items-center gap-2 text-sm text-cyan-300 border border-cyan-400/30 rounded-lg px-4 py-2 hover:bg-cyan-400/10">Nuovo Preventivo</Link>
            <Link data-testid="vr-go-preventivi" to="/preventivi" className="inline-flex items-center gap-2 text-sm text-emerald-300 border border-emerald-400/30 rounded-lg px-4 py-2 hover:bg-emerald-400/10"><FileText className="w-4 h-4" /> Vai ai Preventivi</Link>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="glass rounded-2xl p-5 space-y-5">
            {numero && <div className="text-xs text-slate-500">Da preventivo <span className="text-slate-300 font-mono-tech">{numero}</span></div>}
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Immagine di Esempio Prodotto</label>
              {immagine ? (
                <div data-testid="vr-immagine-preview" className="mt-1 flex items-center gap-3 bg-slate-900/60 border border-slate-700 rounded-xl p-2.5">
                  <img src={immagine.url} alt="anteprima" className="w-14 h-14 rounded-lg object-cover border border-slate-700" />
                  <span className="flex-1 min-w-0 text-sm text-slate-300 truncate">{immagine.name}</span>
                  <button data-testid="vr-immagine-remove" onClick={() => setImmagine(null)} className="w-9 h-9 shrink-0 rounded-lg bg-slate-800 text-slate-400 hover:text-red-300 hover:bg-red-500/15 flex items-center justify-center"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <label data-testid="vr-immagine-upload" className="mt-1 flex items-center justify-center gap-2 w-full bg-slate-900/60 border border-dashed border-slate-600 rounded-xl px-4 py-4 text-slate-400 text-sm cursor-pointer hover:border-emerald-400/50 hover:text-slate-200 transition-colors">
                  {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImagePlus className="w-5 h-5" />}
                  {uploading ? "Caricamento..." : "Carica immagine (JPG, PNG, WEBP)"}
                  <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={onSelectImage} disabled={uploading} />
                </label>
              )}
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Numero WhatsApp Cliente</label>
              <input data-testid="vr-whatsapp" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder="Es. 393331234567" className="mt-1 w-full bg-slate-900/60 border border-slate-700 rounded-xl px-4 py-3 text-slate-100 text-sm font-mono-tech focus:border-emerald-400/60 outline-none" />
              <p className="text-xs text-slate-500 mt-1.5">Con prefisso internazionale, senza + né spazi.</p>
            </div>
          </div>

          <div className="glass rounded-2xl p-5 space-y-4 h-fit">
            <h3 className="font-heading text-lg font-semibold text-slate-100 flex items-center gap-2"><ShoppingBag className="w-5 h-5 text-emerald-400" /> Riepilogo Ordine</h3>
            <div data-testid="vr-righe" className="space-y-2">
              {righe.map((r, i) => (
                <div key={i} data-testid={`vr-riga-${i}`} className="flex items-center justify-between gap-2 bg-slate-900/40 border border-slate-700/60 rounded-lg px-3 py-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-slate-200 truncate">{r.quantita}× {r.tipo_capo} {r.colore} {r.taglia}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-1"><Package className="w-3 h-3" /> {r.sku_neutro} · {eur(r.prezzo_unitario)}/pz</div>
                  </div>
                  <span className="font-mono-tech text-slate-100 text-sm">{eur(r.prezzo_unitario * r.quantita)}</span>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between py-2 border-b border-slate-800/60">
              <span className="text-slate-400 text-sm">Subtotale</span>
              <span data-testid="vr-subtotale" className="font-mono-tech text-slate-200">{eur(subtotale)}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-slate-800/60">
              <span className="text-slate-400 text-sm flex items-center gap-1.5"><Truck className="w-4 h-4" /> {isRitiro ? "Ritiro di persona" : "Spedizione"}</span>
              {spedizione === 0 ? (
                <span data-testid="vr-spedizione" className="inline-flex items-center gap-1.5 text-emerald-300 font-semibold text-sm"><CheckCircle2 className="w-4 h-4" /> {isRitiro ? "GRATIS" : "GRATUITA"}</span>
              ) : (
                <span data-testid="vr-spedizione" className="font-mono-tech text-slate-200">{eur(spedizione)}</span>
              )}
            </div>
            <div className="rounded-xl bg-emerald-400/10 border border-emerald-400/30 p-4 flex items-center justify-between">
              <span className="text-emerald-200 text-sm uppercase tracking-wider font-semibold">Totale Ordine</span>
              <span data-testid="vr-totale" className="font-mono-tech text-3xl font-extrabold text-emerald-300">{eur(totale)}</span>
            </div>

            {!risultato ? (
              <button data-testid="vr-submit-btn" onClick={submit} disabled={loading} className="w-full flex items-center justify-center gap-2.5 py-4 rounded-xl bg-emerald-400 hover:bg-emerald-300 text-slate-900 font-bold text-lg disabled:opacity-60 transition-colors">
                {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : <MessageCircle className="w-6 h-6" />} Crea link ed invia
              </button>
            ) : (
              <div data-testid="vr-risultato" className="space-y-3">
                <div className="rounded-xl bg-slate-900/50 border border-emerald-500/30 p-4">
                  <div className="flex items-center gap-2 text-emerald-300 text-sm font-semibold mb-2"><CheckCircle2 className="w-4 h-4" /> Ordine {risultato.nome || ""} creato su Shopify</div>
                  <a data-testid="vr-invoice-link" href={risultato.invoiceUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-cyan-300 hover:text-cyan-200 text-sm break-all">Link di pagamento <ExternalLink className="w-3.5 h-3.5 shrink-0" /></a>
                </div>
                <a data-testid="vr-whatsapp-link" href={risultato.waUrl} target="_blank" rel="noreferrer" className="w-full flex items-center justify-center gap-2.5 py-4 rounded-xl bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold text-lg transition-colors"><MessageCircle className="w-6 h-6" /> Apri WhatsApp</a>
                <Link data-testid="vr-storico-link" to="/storico-vendite" className="block text-center w-full py-3 rounded-xl border border-slate-700 text-slate-300 hover:bg-slate-700/40 text-sm font-medium">Vai allo Storico Vendite</Link>
              </div>
            )}
            <p className="text-xs text-slate-500">Ogni articolo scala il magazzino del proprio SKU quando il cliente paga il link. Spedizione unica per l'ordine.</p>
          </div>
        </div>
      )}
    </Layout>
  );
}
